import {
  Activity,
  AlertTriangle,
  Bell,
  Code,
  FileWarning,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { Capability } from "@headerhawk/core/plans";

/**
 * The sidebar's tree (ADR 013), as data.
 *
 * The sidebar, the section layouts and `route-integrity.test.ts` all read
 * this, so an entry cannot drift from the routes it names or from the labels
 * the ADR settled. The labels are also the capability page titles on the
 * marketing site (ADR 013, principle 1): a reader who arrives from one finds
 * the thing under the name they read.
 */

/** One view inside a section: a route of its own, drawn as a tab. */
export interface NavTab {
  label: string;
  href: string;
}

export interface NavEntry {
  /** Stable key, used by tests and page objects rather than the label. */
  key: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /**
   * The plan gate on the section as a whole. Only Scripts has one: every
   * other section shows on every plan and gates inside (ADR 013, principle
   * 3).
   */
  capability?: Capability;
  /** The section's own views, when it has more than one. */
  tabs?: NavTab[];
}

const ENTRIES = [
  {
    key: "overview",
    label: "Overview",
    href: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    key: "policy",
    label: "Policy",
    href: "/dashboard/policy",
    icon: AlertTriangle,
    tabs: [
      { label: "Issues", href: "/dashboard/policy" },
      { label: "Changes", href: "/dashboard/policy/changes" },
    ],
  },
  {
    key: "scripts",
    label: "Scripts",
    href: "/dashboard/scripts",
    icon: Code,
    capability: "scripts.inventory",
  },
  {
    key: "headers",
    label: "Headers",
    href: "/dashboard/headers",
    icon: ShieldCheck,
  },
  {
    key: "signals",
    label: "Browser signals",
    href: "/dashboard/signals",
    icon: Activity,
    tabs: [
      { label: "Reliability", href: "/dashboard/signals" },
      { label: "Deprecations", href: "/dashboard/signals/deprecations" },
    ],
  },
  {
    key: "explorer",
    label: "Explorer",
    href: "/dashboard/explorer",
    icon: FileWarning,
  },
  {
    key: "alerts",
    label: "Alerts",
    href: "/dashboard/alerts",
    icon: Bell,
  },
  {
    key: "settings",
    label: "Settings",
    href: "/dashboard/settings",
    icon: Settings,
  },
] as const satisfies readonly NavEntry[];

/** A section's key, so a layout cannot name one that does not exist. */
export type NavSectionKey = (typeof ENTRIES)[number]["key"];

export const NAVIGATION: readonly NavEntry[] = ENTRIES;

/** The entry a path belongs to, so the sidebar highlights a section's tabs. */
export function sectionForPath(pathname: string): NavEntry | undefined {
  const path = pathname.replace(/\/+$/, "") || "/dashboard";
  // Longest href first, so /dashboard/policy beats /dashboard.
  return [...NAVIGATION]
    .sort((a, b) => b.href.length - a.href.length)
    .find((entry) => path === entry.href || path.startsWith(`${entry.href}/`));
}

/** The section a tab row is drawn from, by key. */
export function navEntry(key: NavSectionKey): NavEntry {
  const entry = NAVIGATION.find((candidate) => candidate.key === key);
  if (!entry) throw new Error(`No navigation entry named ${key}`);
  return entry;
}
